import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Select, Input, Modal, Form,
  Typography, Switch, Popconfirm, Tag, message,
} from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import LogoCell from './LogoCell';
import { directoryApi, DirectoryItem, DirectoryCreate, PaginatedResponse, DIRECTORY_CATEGORIES } from '../../api/directory';

const { Title } = Typography;
const { Option } = Select;

type ModalMode = 'create' | 'edit';

const DirectoryList: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<DirectoryItem>>({ items: [], total: 0, page: 1, size: 50, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<{ mode: ModalMode; item?: DirectoryItem } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await directoryApi.list({ search, category: categoryFilter, page, size: 50 });
      setData(res.data);
    } catch {
      message.error('Ошибка загрузки справочника');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    form.resetFields();
    setModal({ mode: 'create' });
  };

  const openEdit = (item: DirectoryItem) => {
    form.setFieldsValue(item);
    setModal({ mode: 'edit', item });
  };

  const handleSubmit = async () => {
    try {
      const values: DirectoryCreate = await form.validateFields();
      setSubmitting(true);
      if (modal?.mode === 'create') {
        await directoryApi.create(values);
        message.success('Организация добавлена');
      } else if (modal?.mode === 'edit' && modal.item) {
        await directoryApi.update(modal.item.id, values);
        message.success('Сохранено');
      }
      setModal(null);
      form.resetFields();
      fetchData();
    } catch (err: any) {
      if (err?.response?.data?.detail) {
        message.error(err.response.data.detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await directoryApi.delete(id);
      message.success('Удалено');
      fetchData();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Логотип',
      dataIndex: 'logo_url',
      width: 150,
      render: (v: string | null | undefined, record: DirectoryItem) => (
        <LogoCell
          url={v}
          name={record.name_ru}
          onUpload={async (file) => { await directoryApi.uploadLogo(record.id, file); await fetchData(); }}
          onRemove={async () => { await directoryApi.deleteLogo(record.id); await fetchData(); }}
        />
      ),
    },
    {
      title: 'Название',
      dataIndex: 'name_ru',
      ellipsis: true,
      render: (v: string, record: DirectoryItem) => (
        <Space>
          {record.is_emergency && <Tag color="red">экстренная</Tag>}
          {v}
          {record.rating ? <Tag>★ {record.rating}</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      width: 120,
      render: (v: string) => DIRECTORY_CATEGORIES[v] || v || '—',
    },
    {
      title: 'Подкатегория',
      dataIndex: 'subcategory',
      width: 130,
      ellipsis: true,
      render: (v: string) => v || '—',
    },
    { title: 'Адрес', dataIndex: 'address', ellipsis: true },
    { title: 'Телефон', dataIndex: 'phone', width: 130 },
    {
      title: 'Действия',
      width: 100,
      render: (_: unknown, record: DirectoryItem) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Удалить организацию?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Справочник организаций</Title>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Поиск по названию"
          prefix={<SearchOutlined />}
          style={{ width: 240 }}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          allowClear
        />
        <Select
          placeholder="Категория"
          style={{ width: 180 }}
          allowClear
          value={categoryFilter}
          onChange={v => { setCategoryFilter(v); setPage(1); }}
        >
          {Object.entries(DIRECTORY_CATEGORIES).map(([k, v]) => (
            <Option key={k} value={k}>{v}</Option>
          ))}
        </Select>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Добавить
        </Button>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data.items}
        loading={loading}
        size="small"
        pagination={{
          current: page,
          pageSize: 50,
          total: data.total,
          onChange: setPage,
          showTotal: (total) => `Всего: ${total}`,
        }}
      />

      <Modal
        open={!!modal}
        title={modal?.mode === 'create' ? 'Новая организация' : 'Редактировать'}
        onOk={handleSubmit}
        onCancel={() => { setModal(null); form.resetFields(); }}
        confirmLoading={submitting}
        okText="Сохранить"
        cancelText="Отмена"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name_ru" label="Название (рус)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name_kk" label="Название (каз)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category" label="Категория">
            <Select allowClear placeholder="Выберите категорию">
              {Object.entries(DIRECTORY_CATEGORIES).map(([k, v]) => (
                <Option key={k} value={k}>{v}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="subcategory" label="Подкатегория">
            <Input placeholder="аптеки, кафе, банкоматы..." />
          </Form.Item>
          <Form.Item name="phone" label="Телефон">
            <Input placeholder="+7 711 000-00-00" />
          </Form.Item>
          <Form.Item name="phone2" label="Телефон 2">
            <Input placeholder="+7 711 000-00-01" />
          </Form.Item>
          <Form.Item name="address" label="Адрес">
            <Input />
          </Form.Item>
          <Form.Item name="website" label="Сайт">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="is_emergency" label="Экстренная служба" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DirectoryList;
